<script setup>
// §8.2 Toast — elevated. Carries the 5-second optimistic undo (Home widget
// spec). Voice (§9): the name survives the flow — the button that said
// "Approve" produces a toast that says "Approved". Undo is an ActionLink-style
// affordance, not a second primary. Floats (shadow allowed, §4).
import { ref, watch, onBeforeUnmount } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  message: { type: String, default: '' },
  duration: { type: Number, default: 5000 }, // the 5-second undo window
  undoable: { type: Boolean, default: true },
})
const emit = defineEmits(['undo', 'dismiss'])

const open = defineModel('open', { default: false })
const progress = ref(100)
let timer = null
let raf = null

function start() {
  stop()
  const t0 = performance.now()
  const tick = (now) => {
    const elapsed = now - t0
    progress.value = Math.max(0, 100 - (elapsed / props.duration) * 100)
    if (elapsed < props.duration) raf = requestAnimationFrame(tick)
  }
  raf = requestAnimationFrame(tick)
  timer = setTimeout(() => (open.value = false), props.duration)
}
function stop() {
  if (timer) clearTimeout(timer)
  if (raf) cancelAnimationFrame(raf)
}
watch(open, (v) => {
  if (v) {
    progress.value = 100
    start()
  } else {
    stop()
    emit('dismiss')
  }
})
onBeforeUnmount(stop)

function undo() {
  emit('undo')
  open.value = false
}
</script>

<template>
  <transition name="toast">
    <div v-if="open" class="l-toast" role="status" @mouseenter="stop" @mouseleave="start">
      <LIcon name="check" class="ok" :size="16" aria-hidden="true" />
      <span class="msg">{{ message }}</span>
      <button v-if="undoable" class="undo" type="button" @click="undo">
        <LIcon name="arrow-rotate-left" :size="13" aria-hidden="true" /> Undo
      </button>
      <button class="dismiss" type="button" aria-label="Dismiss" @click="open = false">
        <LIcon name="xmark" :size="14" aria-hidden="true" />
      </button>
      <div class="track" aria-hidden="true"><div class="fill" :style="{ width: progress + '%' }"></div></div>
    </div>
  </transition>
</template>

<style scoped>
.l-toast {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: 11px var(--sp-3) 11px var(--sp-4);
  background: var(--brand);
  color: var(--on-emphasis);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  width: fit-content;
  overflow: hidden;
}
.ok {
  color: var(--status-approved-fill);
  flex: none;
}
.msg {
  font-size: var(--text-sm);
  font-weight: 500;
}
.undo {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--text-chip);
  font-weight: 600;
  color: var(--on-emphasis);
  background: var(--on-emphasis-fill);
  border: 0;
  border-radius: var(--r-sm);
  padding: 5px 10px;
  min-height: var(--target-min);
  cursor: pointer;
}
.undo:hover {
  background: var(--on-emphasis-fill-hover);
}
.dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--target-min);
  height: var(--target-min);
  color: var(--lotus-primary-200);
  border: 0;
  background: none;
  cursor: pointer;
}
.dismiss:hover {
  color: var(--on-emphasis);
}
.track {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 100%;
  background: var(--on-emphasis-fill);
}
.fill {
  height: 100%;
  background: var(--accent);
}
.toast-enter-active,
.toast-leave-active {
  transition: opacity var(--t-fast) var(--ease), transform var(--t-fast) var(--ease);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
